import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  getUnreadNotificationCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type ListNotificationsQuery,
  type Notification,
  type NotificationListResult,
} from '@/api/notifications.api';

/**
 * React-Query hooks for the notifications inbox (Foundations
 * 2026-07-21). Query keys are namespaced under `['notifications', …]`
 * so a single `invalidateQueries({ queryKey: ['notifications'] })`
 * refreshes the badge, the dropdown, and the full-page list together.
 */

export const NOTIFICATIONS_KEY = ['notifications'] as const;

/** The bell badge — polls every 30s so new notifications surface on their own. */
export function useUnreadNotificationCount(): UseQueryResult<number> {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'unread-count'],
    queryFn: getUnreadNotificationCount,
    refetchInterval: 30_000,
  });
}

/** A single page of the inbox — used by the compact dropdown panel. */
export function useNotificationList(
  query: ListNotificationsQuery = {},
): UseQueryResult<NotificationListResult> {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'list', query],
    queryFn: () => listNotifications(query),
  });
}

/** The full inbox with cursor-based "load older" — used by the page. */
export function useNotificationsInfinite(
  opts: { unreadOnly?: boolean; pageSize?: number } = {},
): UseInfiniteQueryResult<{ pages: NotificationListResult[]; pageParams: unknown[] }> {
  const pageSize = opts.pageSize ?? 20;
  const unreadOnly = opts.unreadOnly ?? false;
  return useInfiniteQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'infinite', { unreadOnly, pageSize }],
    queryFn: ({ pageParam }) => listNotifications({ unreadOnly, limit: pageSize, before: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      const last = lastPage.items.at(-1);
      return lastPage.hasMore && last ? last.createdAt : undefined;
    },
  });
}

/** Mark one notification read, then refresh every notifications query. */
export function useMarkNotificationRead(): UseMutationResult<Notification, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}

/** Mark every unread notification read, then refresh. */
export function useMarkAllNotificationsRead(): UseMutationResult<{ count: number }, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}
